import type {
  NativeSettlementPreviewRequest,
  NativeSettlementPreviewResponse,
  NativeSettlementReportCreateRequest,
  NativeSettlementReport,
  NativeSettlementReportExport,
  NativeSettlementListRequest,
  NativeSettlementReportListResponse,
  NativeSettlementPayoutCreateRequest,
  NativeSettlementPayout,
  NativeSettlementPayoutListResponse,
  NativeSettlementFeeRefreshRequest,
  NativeSettlementFeeRefreshResponse,
  NativeSettlementPdfRequest,
  ProcessorCostEventsRequest,
  ProcessorCostEventsResponse,
  ProcessorCostPeriodRequest,
  ProcessorCostPeriodResponse,
  FeeStatementDetail,
  SettlementBackfillRequest,
  SettlementBackfillResponse,
  SettlementCostParams,
  SettlementCostResponse,
  SettlementCurrentParams,
  SettlementCurrentResponse,
  SettlementLineListParams,
  SettlementLineListResponse,
  SettlementOverviewParams,
  SettlementOverviewResponse,
  SettlementStatementListParams,
  SettlementStatementListResponse,
  ShopFeeConfigParams,
  ShopFeeConfigResponse,
  ShopVisibilityResponse,
  ShopVisibilityUpdateRequest,
  StatementAdjustment,
  StatementAdjustmentCreateRequest,
  StatementAdjustmentListResponse,
  StatementGenerateRequest,
  StatementPayoutUpdateRequest,
  StatementPayoutRecordRequest,
  StatementPayoutListResponse,
  StatementAdvance,
  StatementAdvanceRecordRequest,
  SettlementAdvanceListRequest,
  SettlementAdvanceListResponse,
  StatementPdfParams,
} from '../types';
import type { RequestExtras, RequestFn } from '../client';

function exportHeaders(options: RequestExtras | undefined, accept: string): Record<string, string> {
  const headers = new Headers(options?.headers);
  headers.set('Accept', accept);
  const output: Record<string, string> = {};
  headers.forEach((value, key) => {
    output[key] = value;
  });
  return output;
}

/**
 * Hosted-shop settlement: monthly statements, the live current-period
 * rollup, per-line detail, fee schedules and backfills.
 *
 * Every read takes an explicit `test_mode` — test and live figures must
 * never blend, so the environment lives in the signature rather than in a
 * default. `false` is transmitted, not dropped.
 *
 * Shop-owner responses are redacted server-side: absent platform-fee fields
 * are a permission boundary, not a gap — never re-derive them client-side.
 */
export class Settlement {
  constructor(private readonly request: RequestFn) {}

  /**
   * Read signed refund/dispute processor fees and report availability.
   * Merchant scope and connector grants apply. Empty movements confirm zero
   * only when the source is reported and complete.
   * Supply payment_id without year/month for all history. force_sync reads
   * provider reports for this page; it requires payment_id and limit <= 20,
   * and never issues a payment, refund or dispute action.
   * Omit test_mode for every reporting partition of one payment; period
   * queries default to live. Each source states its reporting partition.
   */
  async listProcessorCostEvents(
    params: ProcessorCostEventsRequest,
    options?: RequestExtras,
  ): Promise<ProcessorCostEventsResponse> {
    return this.request('GET', '/settlement/processor-cost-events', {
      query: {
        profile_id: params.profile_id,
        payment_id: params.payment_id,
        year: params.year,
        month: params.month,
        test_mode: params.test_mode,
        limit: params.limit,
        offset: params.offset,
        force_sync: params.force_sync,
      },
      ...options,
    });
  }

  /**
   * Per-shop settlement rollup for the host merchant: unpaid totals and the
   * running current period, one row per shop.
   *
   * `GET /settlement/overview`
   */
  async overview(
    params: SettlementOverviewParams,
    options?: RequestExtras,
  ): Promise<SettlementOverviewResponse> {
    return this.request('GET', '/settlement/overview', {
      query: { test_mode: params.test_mode },
      ...options,
    });
  }

  /**
   * Live rollup of the current (not yet statemented) period.
   *
   * `GET /settlement/current`
   */
  async current(
    params: SettlementCurrentParams,
    options?: RequestExtras,
  ): Promise<SettlementCurrentResponse> {
    return this.request('GET', '/settlement/current', {
      query: { test_mode: params.test_mode, profile_id: params.profile_id },
      ...options,
    });
  }

  /**
   * What a period's payments cost, and what was left over: gross, the
   * platform fee, hosting fees, what the rails took, and the margin, with a
   * per-connector breakdown.
   *
   * Send `year` and `month` together to report one UTC calendar month, or
   * neither for the running month so far.
   *
   * **Host-only.** The response is the host's cost base, which a shop owner
   * must never see, so a profile-scoped caller is refused with a 403 rather
   * than given a redacted shell. Gate the surface on the caller's scope
   * instead of calling it and handling the failure. Connector-restricted
   * roles cannot read this merchant-wide aggregate; fee-event reads retain
   * their connector grants.
   *
   * Two things not to flatten when rendering the result:
   * `margin_usd` is absent — not zero — whenever `margin_quality` is
   * `'unknown'`, and `unlined_captured_attempt_count` (cost definitely
   * missing) means something different from `unlined_unresolved_attempt_count`
   * (mostly ordinary abandonment).
   *
   * `GET /settlement/cost`
   *
   * @example
   * ```typescript
   * const cost = await delopay.settlement.cost({ test_mode: false, year: 2026, month: 7 });
   * if (cost.margin_quality === 'unknown') {
   *   // cost.margin_usd is absent — say so, do not render 0.00
   * }
   * ```
   */
  async cost(
    params?: SettlementCostParams,
    options?: RequestExtras,
  ): Promise<SettlementCostResponse> {
    return this.request('GET', '/settlement/cost', {
      query: {
        profile_id: params?.profile_id,
        test_mode: params?.test_mode,
        year: params?.year,
        month: params?.month,
      },
      ...options,
    });
  }

  /**
   * What a period's payments cost at the rails, grouped by shop, connector
   * and payment method. Answers for **every** merchant, including one that
   * hosts nobody — it reads the per-attempt settlement record rather than the
   * settlement lines, which exist only where hosting fees are configured.
   *
   * A sibling of {@link Settlement.cost} rather than a widening of it. That
   * endpoint is a profit statement whose revenue term is hosting income, and
   * a merchant who hosts nobody has none. This publishes cost and never
   * margin.
   *
   * Send `year` and `month` together to report one UTC calendar month, or
   * neither for the running month so far.
   *
   * **The response carries no period total, and a caller must not compute
   * one here.** Every figure keeps the unit the rail reported it in, and the
   * unit is part of each bucket's key, so adding two buckets is not addition.
   * Convert caller-side if you need one number, and say which rates you used.
   * The counts are totalled, because a count has no unit.
   *
   * Two distinctions not to flatten when rendering a bucket:
   * `cost_amount` covers `reported_count + estimated_count` attempts out of
   * `attempt_count` and is a lower bound whenever those disagree; and
   * `unavailable_count` (the rail says it has no figure) means something
   * different from `unwritten_count` (a gap in our own recording).
   *
   * **Merchant scope.** A token scoped to a single shop, a role narrowed to
   * specific connector accounts, or a role without the settlement-read
   * permission group is refused `403`: what a rail charged is the merchant's
   * own cost base, and here it is the whole response, so there is nothing
   * coherent to redact down to. Gate the surface on the caller's scope rather
   * than calling it and handling the failure.
   *
   * `GET /settlement/processor-cost`
   *
   * @example
   * ```typescript
   * const period = await delopay.settlement.processorCost({
   *   test_mode: false,
   *   year: 2026,
   *   month: 7,
   * });
   * for (const bucket of period.buckets) {
   *   // Each bucket is its own unit — render per bucket, never a sum.
   *   console.log(bucket.connector, bucket.cost_amount, bucket.cost_currency);
   * }
   * ```
   */
  async processorCost(
    params?: ProcessorCostPeriodRequest,
    options?: RequestExtras,
  ): Promise<ProcessorCostPeriodResponse> {
    return this.request('GET', '/settlement/processor-cost', {
      query: {
        profile_id: params?.profile_id,
        test_mode: params?.test_mode,
        year: params?.year,
        month: params?.month,
      },
      ...options,
    });
  }

  /**
   * List generated settlement statements, newest first.
   *
   * `GET /settlement/statements`
   */
  async listStatements(
    params: SettlementStatementListParams,
    options?: RequestExtras,
  ): Promise<SettlementStatementListResponse> {
    return this.request('GET', '/settlement/statements', {
      query: {
        test_mode: params.test_mode,
        profile_id: params.profile_id,
        limit: params.limit,
        offset: params.offset,
      },
      ...options,
    });
  }

  /**
   * One statement with its per-connector/currency breakdown.
   *
   * `GET /settlement/statements/{statementId}`
   */
  async retrieveStatement(
    statementId: string,
    options?: RequestExtras,
  ): Promise<FeeStatementDetail> {
    return this.request(
      'GET',
      `/settlement/statements/${encodeURIComponent(statementId)}`,
      options,
    );
  }

  /**
   * Generate (or regenerate) the statement for one shop and calendar month.
   *
   * `POST /settlement/statements/generate`
   */
  async generateStatement(
    params: StatementGenerateRequest,
    options?: RequestExtras,
  ): Promise<FeeStatementDetail> {
    return this.request('POST', '/settlement/statements/generate', {
      body: params,
      ...options,
    });
  }

  /**
   * Record payout progress on a statement (`unpaid` / `partial` / `paid`).
   *
   * Subject to the caller's `settlement_payout` operation limit — a
   * per-operation ceiling, a windowed total, a windowed count, or any
   * combination: an over-limit call fails with `DE_01` and nothing is
   * recorded. There is no approval route out of it — four-eyes needs an
   * executor that can run the operation once somebody says yes, and only
   * refunds have one, so a settlement rule can only block.
   *
   * `POST /settlement/statements/{statementId}/payout`
   */
  async updateStatementPayout(
    statementId: string,
    params: StatementPayoutUpdateRequest,
    options?: RequestExtras,
  ): Promise<FeeStatementDetail> {
    return this.request(
      'POST',
      `/settlement/statements/${encodeURIComponent(statementId)}/payout`,
      { body: params, ...options },
    );
  }

  /** Append one external transfer record. Send a stable idempotency_key for safe retries. */
  async recordStatementPayout(
    statementId: string,
    params: StatementPayoutRecordRequest,
    options?: RequestExtras,
  ): Promise<FeeStatementDetail> {
    return this.request(
      'POST',
      `/settlement/statements/${encodeURIComponent(statementId)}/payout`,
      {
        body: params,
        ...options,
        bodyIdempotencyKey: params.idempotency_key ?? null,
      },
    );
  }

  /** Read individual transfer records, their signed total, and any overpayment. */
  async listStatementPayouts(
    statementId: string,
    options?: RequestExtras,
  ): Promise<StatementPayoutListResponse> {
    return this.request(
      'GET',
      `/settlement/statements/${encodeURIComponent(statementId)}/payouts`,
      options,
    );
  }

  /** Record a transfer against a shop's month before its statement exists. */
  async recordAdvance(
    params: StatementAdvanceRecordRequest,
    options?: RequestExtras,
  ): Promise<StatementAdvance> {
    return this.request('POST', '/settlement/advances', {
      body: params,
      ...options,
      bodyIdempotencyKey: params.idempotency_key ?? null,
    });
  }

  async listAdvances(
    params: SettlementAdvanceListRequest,
    options?: RequestExtras,
  ): Promise<SettlementAdvanceListResponse> {
    return this.request('GET', '/settlement/advances', {
      query: {
        profile_id: params.profile_id,
        year: params.year,
        month: params.month,
        test_mode: params.test_mode,
      },
      ...options,
    });
  }

  /**
   * Export a statement as PDF. Returns the raw PDF bytes as a `Blob`, with
   * the same auth, retries and error handling as every other call — persist
   * or object-URL it caller-side.
   *
   * `GET /settlement/statements/{statementId}/pdf`
   *
   * @example
   * ```typescript
   * const pdf = await delopay.settlement.downloadStatementPdf('stmt_1', {
   *   currency: 'EUR',
   *   include_transactions: true,
   * });
   * const url = URL.createObjectURL(pdf);
   * ```
   */
  async downloadStatementPdf(
    statementId: string,
    params?: StatementPdfParams,
    options?: RequestExtras,
  ): Promise<Blob> {
    return this.request('GET', `/settlement/statements/${encodeURIComponent(statementId)}/pdf`, {
      query: {
        currency: params?.currency,
        include_transactions: params?.include_transactions,
      },
      responseType: 'blob',
      ...options,
    });
  }

  /**
   * The individual settled attempts of one shop's calendar month.
   *
   * `GET /settlement/lines`
   */
  async listLines(
    params: SettlementLineListParams,
    options?: RequestExtras,
  ): Promise<SettlementLineListResponse> {
    return this.request('GET', '/settlement/lines', {
      query: {
        profile_id: params.profile_id,
        year: params.year,
        month: params.month,
        test_mode: params.test_mode,
        limit: params.limit,
        offset: params.offset,
      },
      ...options,
    });
  }

  /**
   * The fee schedules that currently apply to a shop.
   *
   * `GET /settlement/fee-config`
   */
  async feeConfig(
    params: ShopFeeConfigParams,
    options?: RequestExtras,
  ): Promise<ShopFeeConfigResponse> {
    return this.request('GET', '/settlement/fee-config', {
      query: { profile_id: params.profile_id },
      ...options,
    });
  }

  /**
   * Enqueue a settlement-line backfill over historical attempts. Attempts
   * already covered by a line are always skipped.
   *
   * `POST /settlement/backfill`
   */
  async backfill(
    params?: SettlementBackfillRequest,
    options?: RequestExtras,
  ): Promise<SettlementBackfillResponse> {
    return this.request('POST', '/settlement/backfill', { body: params, ...options });
  }

  /**
   * Toggle whether a shop's owner can see their own settlement figures.
   *
   * `POST /settlement/shops/visibility`
   */
  async setShopVisibility(
    params: ShopVisibilityUpdateRequest,
    options?: RequestExtras,
  ): Promise<ShopVisibilityResponse> {
    return this.request('POST', '/settlement/shops/visibility', {
      body: params,
      ...options,
    });
  }

  /**
   * Manual adjustments recorded on a statement.
   *
   * `GET /settlement/statements/{statementId}/adjustments`
   */
  async listStatementAdjustments(
    statementId: string,
    options?: RequestExtras,
  ): Promise<StatementAdjustmentListResponse> {
    return this.request(
      'GET',
      `/settlement/statements/${encodeURIComponent(statementId)}/adjustments`,
      options,
    );
  }

  /**
   * Add a manual adjustment to a statement. Positive `amount_usd` charges
   * the shop (reducing their payout); negative credits them.
   *
   * Subject to the caller's `settlement_adjustment` operation limit (amount
   * dimensions only): an over-limit call fails with `DE_01` and no adjustment
   * is added. A settlement rule can only block — approval is refund-only, for
   * the reason given on `updateStatementPayout()`.
   *
   * `POST /settlement/statements/{statementId}/adjustments`
   */
  async createStatementAdjustment(
    statementId: string,
    params: StatementAdjustmentCreateRequest,
    options?: RequestExtras,
  ): Promise<StatementAdjustment> {
    return this.request(
      'POST',
      `/settlement/statements/${encodeURIComponent(statementId)}/adjustments`,
      { body: params, ...options },
    );
  }

  /**
   * Remove a manual adjustment from a statement.
   *
   * `DELETE /settlement/statements/{statementId}/adjustments/{adjustmentId}`
   */
  async deleteStatementAdjustment(
    statementId: string,
    adjustmentId: string,
    options?: RequestExtras,
  ): Promise<void> {
    return this.request(
      'DELETE',
      `/settlement/statements/${encodeURIComponent(statementId)}/adjustments/${encodeURIComponent(adjustmentId)}`,
      options,
    );
  }

  /**
   * Calculate a period in its transaction currency. Requires a host-level
   * dashboard JWT and settlement read permission. Profile users and roles
   * restricted to selected connector accounts cannot read this aggregate.
   * Rates apply once to the period total. Unknown or estimated fees keep
   * dependent amounts provisional. Native money values remain integer strings.
   */
  async previewNative(
    params: NativeSettlementPreviewRequest,
    options?: RequestExtras,
  ): Promise<NativeSettlementPreviewResponse> {
    return this.request('POST', '/settlement/native/preview', { body: params, ...options });
  }

  /**
   * Recalculate and freeze an immutable report version. Requires settlement
   * edit permission. Explicitly allow provisional figures when source fees
   * are incomplete. A report records a calculation and sends no money.
   * Reuse the same idempotency key when retrying the same request.
   */
  async createNativeReport(
    params: NativeSettlementReportCreateRequest,
    options?: RequestExtras,
  ): Promise<NativeSettlementReport> {
    return this.request('POST', '/settlement/native/reports', {
      body: params,
      ...options,
      bodyIdempotencyKey: params.idempotency_key,
    });
  }

  async retrieveNativeReport(
    reportId: string,
    options?: RequestExtras,
  ): Promise<NativeSettlementReport> {
    return this.request(
      'GET',
      `/settlement/native/reports/${encodeURIComponent(reportId)}`,
      options,
    );
  }

  /** List report summaries without fetching every source row in each saved snapshot. */
  async listNativeReports(
    params: NativeSettlementListRequest,
    options?: RequestExtras,
  ): Promise<NativeSettlementReportListResponse> {
    return this.request('GET', '/settlement/native/reports', {
      query: {
        profile_id: params.profile_id,
        currency: params.currency,
        test_mode: params.test_mode,
        limit: params.limit,
        offset: params.offset,
      },
      ...options,
    });
  }

  /**
   * Record a transfer already made, under settlement write permission and
   * the caller's payout limits. This method never sends money. Native minor
   * units remain strings; negative records must name a same-scope reversal.
   * These records belong to the native ledger, separate from monthly USD
   * statement payouts and advances.
   */
  async recordNativePayout(
    params: NativeSettlementPayoutCreateRequest,
    options?: RequestExtras,
  ): Promise<NativeSettlementPayout> {
    return this.request('POST', '/settlement/native/payouts', {
      body: params,
      ...options,
      bodyIdempotencyKey: params.idempotency_key,
    });
  }

  async listNativePayouts(
    params: NativeSettlementListRequest,
    options?: RequestExtras,
  ): Promise<NativeSettlementPayoutListResponse> {
    return this.request('GET', '/settlement/native/payouts', {
      query: {
        profile_id: params.profile_id,
        currency: params.currency,
        test_mode: params.test_mode,
        limit: params.limit,
        offset: params.offset,
      },
      ...options,
    });
  }

  /** Refresh a bounded page of provider fee evidence. Follow next_offset while has_more is true. */
  async refreshNativeFees(
    params: NativeSettlementFeeRefreshRequest,
    options?: RequestExtras,
  ): Promise<NativeSettlementFeeRefreshResponse> {
    return this.request('POST', '/settlement/native/fees/refresh', { body: params, ...options });
  }

  async downloadNativeReportPdf(
    reportId: string,
    params?: NativeSettlementPdfRequest,
    options?: RequestExtras,
  ): Promise<Blob> {
    return this.request('GET', `/settlement/native/reports/${encodeURIComponent(reportId)}/pdf`, {
      query: { include_transactions: params?.include_transactions },
      responseType: 'blob',
      ...options,
    });
  }

  /** Download the frozen source rows and calculation as CSV. */
  async downloadNativeReportCsv(reportId: string, options?: RequestExtras): Promise<Blob> {
    return this.request(
      'GET',
      `/settlement/native/reports/${encodeURIComponent(reportId)}/export`,
      {
        ...options,
        headers: exportHeaders(options, 'text/csv'),
        responseType: 'blob',
      },
    );
  }

  /** Export the frozen report as JSON, excluding host account-level costs. */
  async exportNativeReportJson(
    reportId: string,
    options?: RequestExtras,
  ): Promise<NativeSettlementReportExport> {
    return this.request(
      'GET',
      `/settlement/native/reports/${encodeURIComponent(reportId)}/export`,
      {
        ...options,
        headers: exportHeaders(options, 'application/json'),
      },
    );
  }
}
